import { Wishlist } from '@theme/wishlist';

class WishlistSection extends HTMLElement {
  /**
   * The observer for lazy loading the section
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadWishlist();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes (for theme editor)
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      if (mutation.attributeName === 'data-error') continue;

      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      if (
        mutation.attributeName === 'data-search-performed' &&
        this.dataset.searchPerformed === 'true'
      )
        continue;

      this.#loadWishlist();
      break;
    }
  });

  /**
   * An abort controller for active fetches
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** @type {boolean} Whether a load is in progress (prevents re-entrance from our own events) */
  #isLoading = false;

  /** @type {boolean} Whether a removal just happened (suppresses follow-up refetch) */
  #removalInProgress = false;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });

    document.addEventListener('apa:wishlist:updated', this.#onWishlistChanged);
    document.addEventListener('apa:wishlist:count', this.#onWishlistChanged);
    document.addEventListener('apa:wishlist:removed', this.#onProductRemoved);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();

    document.removeEventListener('apa:wishlist:updated', this.#onWishlistChanged);
    document.removeEventListener('apa:wishlist:count', this.#onWishlistChanged);
    document.removeEventListener('apa:wishlist:removed', this.#onProductRemoved);
  }

  /**
   * Refetch when wishlist changes (handles both apa:wishlist:updated and apa:wishlist:count).
   * Skipped during loading (own clearItems) and after removals (DOM already updated).
   */
  #onWishlistChanged = () => {
    if (this.#isLoading || this.#removalInProgress) return;
    this.dataset.searchPerformed = 'false';
    this.#loadWishlist();
  };

  /**
   * Instantly remove a product card from the DOM when removed from wishlist.
   * Suppresses follow-up count/updated events from triggering a refetch.
   * @param {CustomEvent} event - Expected detail: { productId: string }
   */
  #onProductRemoved = (event) => {
    const productId = event.detail?.productId;
    if (!productId) return;

    const cards = this.querySelectorAll(`product-card[data-product-id="${productId}"]`);
    if (cards.length === 0) return;

    // Suppress follow-up count/updated events — DOM removal is sufficient
    this.#removalInProgress = true;

    const animations = [...cards].map((card) => {
      const listItem = card.closest('.resource-list__item');
      const slide = listItem?.closest('slideshow-slide');
      const target = slide || listItem || card;

      return target
        .animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, easing: 'ease-out' })
        .finished.then(() => target.remove());
    });

    Promise.all(animations).then(() => {
      this.#removalInProgress = false;

      if (!this.querySelector('product-card')) {
        this.#handleEmpty();
      }
    });
  };

  /**
   * Load wishlist products
   */
  #loadWishlist() {
    const { sectionId, searchPerformed, customerLoggedIn } = this.dataset;
    const id = this.id;

    if (!id || !sectionId) {
      throw new Error('Section ID and an ID attribute are required');
    }

    if (searchPerformed === 'true') {
      return;
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    this.#isLoading = true;

    const isLoggedIn = customerLoggedIn === 'true';

    this.#getProductIds(isLoggedIn)
      .then((productIds) => {
        if (!productIds || productIds.length === 0) {
          this.#handleEmpty();
          return;
        }

        return this.#fetchSection(productIds, sectionId);
      })
      .then((result) => {
        if (!result) return;

        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        // Content is from Shopify's Section Rendering API (same-origin, server-rendered)
        html.innerHTML = result.data || '';
        const wishlistEl = html.querySelector(`wishlist-section[id="${id}"]`);

        if (wishlistEl?.innerHTML && wishlistEl.innerHTML.trim().length) {
          const hasProducts = wishlistEl.querySelector('.resource-list__item');
          if (hasProducts) {
            this.dataset.searchPerformed = 'true';
            // Server-rendered content from Shopify's Section Rendering API
            this.innerHTML = wishlistEl.innerHTML;
          } else {
            this.#handleEmpty();
          }
        } else {
          this.#handleEmpty();
        }
      })
      .catch((e) => {
        if (e instanceof Error && e.name === 'AbortError') return;
        this.#handleError(e);
      })
      .finally(() => {
        this.#isLoading = false;
      });
  }

  /**
   * Get product IDs from the appropriate source.
   * Handles login sync: if logged in and localStorage has items, POST them to the API first.
   * @param {boolean} isLoggedIn
   * @returns {Promise<string[]>}
   */
  async #getProductIds(isLoggedIn) {
    if (!isLoggedIn) {
      return Wishlist.getItems();
    }

    const signal = this.#activeFetch?.signal;

    // Login sync: push localStorage items to API, then clear
    const localItems = Wishlist.getItems();
    if (localItems.length > 0) {
      try {
        await fetch('/apps/apa/api/wishlist/add-items', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productIds: localItems }),
          signal,
        });
        Wishlist.clearItems();
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') throw e;
        // Sync failed — continue with API fetch
      }
    }

    const response = await fetch('/apps/apa/api/wishlist', { signal });
    if (!response.ok) {
      throw new Error(`Wishlist API returned ${response.status}`);
    }

    const data = await response.json();
    return (data.products || []).map((/** @type {{ id: string | number }} */ p) => String(p.id));
  }

  /**
   * Fetches section HTML via search + Section Rendering API
   * @param {string[]} productIds - Array of product IDs
   * @param {string} sectionId - The section ID
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchSection(productIds, sectionId) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), { signal: this.#activeFetch?.signal });
    if (!response.ok) {
      return { success: false, status: response.status };
    }

    const text = await response.text();
    return { success: true, data: text };
  }

  /**
   * Handle empty state (show empty message, hide skeleton/products)
   */
  #handleEmpty() {
    const skeleton = this.querySelector('.wishlist-section__skeleton');
    if (skeleton) skeleton.hidden = true;

    for (const resourceList of this.querySelectorAll('.resource-list')) {
      resourceList.hidden = true;
    }

    for (const header of this.querySelectorAll('.resource-list__header')) {
      header.hidden = true;
    }

    const emptyState = this.querySelector('.wishlist-section__empty');
    if (emptyState) emptyState.hidden = false;
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Wishlist section error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading wishlist';
  }
}

if (!customElements.get('wishlist-section')) {
  customElements.define('wishlist-section', WishlistSection);
}
